import { useEffect, useRef, useState, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';

export const Waveform = ({ url, isPlaying, currentTime, duration, onSeek }) => {
  const containerRef = useRef(null);
  const wavesurferRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [peaks, setPeaks] = useState([]);
  const animationFrameRef = useRef(null);
  const isDestroyedRef = useRef(false);

  // Generate smooth animated bars
  const generatePeaks = useCallback(() => {
    const numBars = 80;
    const newPeaks = [];
    for (let i = 0; i < numBars; i++) {
      // Create a wave-like pattern
      const baseHeight = 0.3 + Math.sin(i * 0.3) * 0.2;
      const variation = Math.random() * 0.4;
      newPeaks.push(Math.min(1, baseHeight + variation));
    }
    return newPeaks;
  }, []);

  useEffect(() => {
    if (!containerRef.current || !url) return;

    setHasError(false);
    setIsReady(false);
    setPeaks(generatePeaks());
    isDestroyedRef.current = false;

    let wavesurfer = null;
    
    try {
      // Create WaveSurfer instance with enhanced styling
      wavesurfer = WaveSurfer.create({
        container: containerRef.current,
        waveColor: '#1a1a1a',
        progressColor: '#00CCFF',
        cursorColor: '#ffff00',
        cursorWidth: 2,
        barWidth: 3,
        barGap: 2,
        barRadius: 2,
        height: 48,
        normalize: true,
        interact: true,
        hideScrollbar: true,
        backend: 'WebAudio',
        responsive: true,
        barMinHeight: 2,
      });

      wavesurfer.load(url);

      wavesurfer.on('ready', () => {
        if (!isDestroyedRef.current) {
          setIsReady(true);
        }
      });

      wavesurfer.on('error', (err) => {
        if (!isDestroyedRef.current) {
          console.warn('Waveform load error, using fallback:', err?.message || err);
          setHasError(true);
        }
      });

      wavesurfer.on('interaction', () => {
        if (!isDestroyedRef.current && wavesurfer.getDuration() > 0) {
          const seekTime = wavesurfer.getCurrentTime();
          onSeek(seekTime);
        }
      });

      wavesurferRef.current = wavesurfer;
    } catch (err) {
      console.warn('WaveSurfer creation error:', err);
      setHasError(true);
    }

    return () => {
      isDestroyedRef.current = true;
      if (animationFrameRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (wavesurfer) {
        try {
          wavesurfer.destroy();
        } catch (e) {
          // Ignore destroy errors
        }
      }
      wavesurferRef.current = null;
    };
  }, [url, generatePeaks, onSeek]);

  // Sync progress with external currentTime
  useEffect(() => {
    if (wavesurferRef.current && isReady && duration > 0 && !isDestroyedRef.current) {
      const progress = currentTime / duration;
      if (progress >= 0 && progress <= 1) {
        try {
          wavesurferRef.current.seekTo(progress);
        } catch (e) {
          // Ignore seek errors
        }
      }
    }
  }, [currentTime, duration, isReady]);

  if (!url) {
    return null;
  }

  // Custom animated fallback when WaveSurfer fails
  if (hasError) {
    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
    const progressIndex = Math.floor((progress / 100) * peaks.length);
    
    return (
      <div 
        className="w-full h-12 flex items-center gap-[2px] cursor-pointer px-1"
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const percentage = x / rect.width;
          onSeek(percentage * duration);
        }}
        data-testid="waveform-fallback"
      >
        {peaks.map((height, index) => {
          const isPlayed = index <= progressIndex;
          const isCurrent = index === progressIndex;
          return (
            <div
              key={`peak-${index}`}
              className={`flex-1 rounded-sm transition-all duration-150 ${
                isPlaying && isCurrent ? 'animate-pulse' : ''
              }`}
              style={{
                height: `${height * 100}%`,
                minHeight: '4px',
                maxHeight: '100%',
                backgroundColor: isPlayed ? '#00CCFF' : '#1a1a1a',
                boxShadow: isCurrent ? '0 0 8px #00CCFF' : 'none'
              }}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className="relative w-full">
      {!isReady && (
        <div className="absolute inset-0 flex items-center gap-[2px] px-1">
          {peaks.map((height, index) => (
            <div
              key={`peak-loading-${index}`}
              className="flex-1 bg-[#1a1a1a] rounded-sm animate-pulse"
              style={{
                height: `${height * 48}px`,
                minHeight: '4px',
                animationDelay: `${index * 20}ms`
              }}
            />
          ))}
        </div>
      )}
      <div 
        ref={containerRef} 
        className={`w-full transition-opacity duration-500 ${isReady ? 'opacity-100' : 'opacity-0'}`}
        data-testid="waveform"
      />
    </div>
  );
};
